// The owner's schedule choice: a few plain presets, or a cron of their own, in a time zone. It only
// builds a cron and a zone; the server's scheduler decides whether that is allowed (at most every 15
// minutes) and answers with the next runs. Nothing here predicts a run time.
import { useId, useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import appTrackerCopy from '@/i18n/ns/app-tracker';
import { Button } from '@/components/ui/button';
import type { AppSchedulePresentation } from '../schedule-presentation';
import type { ScheduleChoice } from '../schedule-api';
import { scheduleWords, weekdayName } from './schedule-words';

export type SchedulePreset =
  | 'every15'
  | 'every30'
  | 'hourly'
  | 'daily'
  | 'weekdays'
  | 'weekly'
  | 'custom';
export type ScheduleDraftChoice = {
  preset: SchedulePreset;
  time: string;
  day: number;
  cron: string;
  timezone: string;
};

const PRESETS: readonly SchedulePreset[] = [
  'every15',
  'every30',
  'hourly',
  'daily',
  'weekdays',
  'weekly',
  'custom',
];
const PRESET_COPY = {
  every15: 'presetEvery15',
  every30: 'presetEvery30',
  hourly: 'presetHourly',
  daily: 'presetDaily',
  weekdays: 'presetWeekdays',
  weekly: 'presetWeekly',
  custom: 'presetCustom',
} as const;
const TIMED = new Set<SchedulePreset>(['daily', 'weekdays', 'weekly']);
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/u;

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The zones a person can pick: every IANA zone the browser knows, with the current one first. */
export function timeZones(current: string) {
  let all: string[] = [];
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    all = Array.isArray(supported) ? supported : [];
  } catch {
    all = [];
  }
  return [...new Set([current, browserTimeZone(), 'UTC', ...all])];
}

/** The picker's starting point from the schedule on show (or the declared one). */
export function draftFrom(schedule: AppSchedulePresentation | null): ScheduleDraftChoice {
  const base = {
    time: '07:00',
    day: 1,
    cron: schedule?.cron ?? '',
    timezone: schedule?.timezone ?? browserTimeZone(),
  };
  if (!schedule) return { ...base, preset: 'daily' };
  if (schedule.cadence === 'interval') {
    const preset =
      schedule.intervalMinutes === 15
        ? 'every15'
        : schedule.intervalMinutes === 30
          ? 'every30'
          : schedule.intervalMinutes === 60
            ? 'hourly'
            : 'custom';
    return { ...base, preset };
  }
  if (schedule.cadence === 'daily') return { ...base, preset: 'daily', time: schedule.time };
  if (schedule.cadence === 'weekly') {
    const weekdays =
      schedule.days.length === 5 && [1, 2, 3, 4, 5].every((day) => schedule.days.includes(day));
    if (weekdays) return { ...base, preset: 'weekdays', time: schedule.time };
    if (schedule.days.length === 1)
      return { ...base, preset: 'weekly', time: schedule.time, day: schedule.days[0] };
  }
  return { ...base, preset: 'custom' };
}

/** The cron for a draft, or null while the draft is incomplete. A custom cron is sent as written. */
export function cronFor(draft: ScheduleDraftChoice): string | null {
  if (draft.preset === 'every15') return '*/15 * * * *';
  if (draft.preset === 'every30') return '*/30 * * * *';
  if (draft.preset === 'hourly') return '0 * * * *';
  if (draft.preset === 'custom') {
    const fields = draft.cron.trim().split(/\s+/u);
    return fields.length === 5 && fields.every(Boolean) ? fields.join(' ') : null;
  }
  const time = TIME.exec(draft.time);
  if (!time) return null;
  const at = `${Number(time[2])} ${Number(time[1])}`;
  if (draft.preset === 'daily') return `${at} * * *`;
  if (draft.preset === 'weekdays') return `${at} * * 1-5`;
  return `${at} * * ${draft.day}`;
}

function previewOf(draft: ScheduleDraftChoice): AppSchedulePresentation | null {
  const timezone = draft.timezone;
  if (draft.preset === 'every15') return { cadence: 'interval', intervalMinutes: 15, timezone };
  if (draft.preset === 'every30') return { cadence: 'interval', intervalMinutes: 30, timezone };
  if (draft.preset === 'hourly') return { cadence: 'interval', intervalMinutes: 60, timezone };
  if (!TIME.test(draft.time) || draft.preset === 'custom') return null;
  if (draft.preset === 'daily') return { cadence: 'daily', time: draft.time, timezone };
  const days = draft.preset === 'weekdays' ? [1, 2, 3, 4, 5] : [draft.day];
  return { cadence: 'weekly', time: draft.time, days, timezone };
}

export function SchedulePicker(props: {
  initial: AppSchedulePresentation | null;
  saving: boolean;
  /** The server's refusal of the last save, already worded. */
  error: string | null;
  onSave: (choice: ScheduleChoice) => void;
  onCancel: () => void;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const [draft, setDraft] = useState(() => draftFrom(props.initial));
  const [zones] = useState(() => timeZones(draft.timezone));
  const headingId = useId();
  const cronHelpId = useId();
  const set = <K extends keyof ScheduleDraftChoice>(key: K, value: ScheduleDraftChoice[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const cron = cronFor(draft);
  const preview = previewOf(draft);
  // One radio group: arrows move the choice, Tab leaves the group (WAI-ARIA radio pattern).
  const move = (from: SchedulePreset, step: number, group: HTMLElement | null) => {
    const next = PRESETS[(PRESETS.indexOf(from) + step + PRESETS.length) % PRESETS.length];
    set('preset', next);
    group?.querySelector<HTMLElement>(`[data-preset="${next}"]`)?.focus();
  };
  return (
    <div className="pc-tracker-schedule pc-schedule-picker" data-testid="schedule-picker">
      <form
        aria-label={copy.pickerHeading}
        onSubmit={(event) => {
          event.preventDefault();
          if (cron && !props.saving) props.onSave({ cron, timezone: draft.timezone });
        }}
      >
        <p className="pc-schedule-label" id={headingId}>
          {copy.pickerHeading}
        </p>
        <div className="pc-tracker-chips" role="radiogroup" aria-labelledby={headingId}>
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              role="radio"
              aria-checked={draft.preset === preset}
              tabIndex={draft.preset === preset ? 0 : -1}
              data-preset={preset}
              data-testid={`schedule-preset-${preset}`}
              onClick={() => set('preset', preset)}
              onKeyDown={(event) => {
                const step =
                  event.key === 'ArrowRight' || event.key === 'ArrowDown'
                    ? 1
                    : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                      ? -1
                      : 0;
                if (!step) return;
                event.preventDefault();
                move(preset, step, event.currentTarget.parentElement);
              }}
            >
              {copy[PRESET_COPY[preset]]}
            </button>
          ))}
        </div>
        <div className="pc-schedule-fields" data-preset={draft.preset}>
          {draft.preset === 'weekly' && (
            <label>
              {copy.pickerDay}
              <select
                data-testid="schedule-picker-day"
                value={String(draft.day)}
                onChange={(event) => set('day', Number(event.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                  <option key={day} value={String(day)}>
                    {weekdayName(day, locale)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {TIMED.has(draft.preset) && (
            <label>
              {copy.pickerTime}
              <input
                type="time"
                data-testid="schedule-picker-time"
                value={draft.time}
                onChange={(event) => set('time', event.target.value)}
                onInput={(event) => set('time', event.currentTarget.value)}
              />
            </label>
          )}
          {draft.preset === 'custom' && (
            <label className="pc-schedule-cron-field">
              {copy.pickerCron}
              <input
                className="pc-schedule-cron-input"
                data-testid="schedule-picker-cron"
                spellCheck={false}
                autoComplete="off"
                placeholder="0 9 1 * *"
                aria-describedby={cronHelpId}
                value={draft.cron}
                onChange={(event) => set('cron', event.target.value)}
                onInput={(event) => set('cron', event.currentTarget.value)}
              />
              <small className="pc-tracker-note" id={cronHelpId}>
                {copy.pickerCronHelp}
              </small>
            </label>
          )}
          <label className="pc-schedule-zone-field">
            {copy.scheduleTimezone}
            <select
              data-testid="schedule-picker-zone"
              value={draft.timezone}
              onChange={(event) => set('timezone', event.target.value)}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="pc-tracker-note" data-testid="schedule-picker-preview" aria-live="polite">
          {preview
            ? `${scheduleWords(preview, copy, locale)} (${draft.timezone})`
            : copy.pickerCustomPreview}
        </p>
        {props.error && (
          <small role="alert" data-testid="schedule-picker-error">
            {props.error}
          </small>
        )}
        <div className="pc-tracker-actions">
          <Button
            type="submit"
            size="sm"
            data-testid="schedule-picker-save"
            disabled={!cron || props.saving}
          >
            {props.saving ? copy.scheduleSaving : copy.scheduleSave}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => props.onCancel()}>
            {copy.pickerCancel}
          </Button>
        </div>
      </form>
    </div>
  );
}
